import { type ConsentTier, getConsentTier } from "@/lib/consentRegions";

export interface GeoResult {
    country: string | null;
    consentTier: ConsentTier;
}

type GeoCountryListener = (country: string) => void;

let cachedGeo: GeoResult | null = null;
let inflightGeoRequest: Promise<GeoResult> | null = null;
const listeners = new Set<GeoCountryListener>();

function notifyListeners(country: string) {
    listeners.forEach((listener) => listener(country));
}

export function getCachedGeoCountry(): string | null {
    return cachedGeo?.country ?? null;
}

export function subscribeGeoCountry(listener: GeoCountryListener): () => void {
    listeners.add(listener);
    if (cachedGeo?.country) {
        listener(cachedGeo.country);
    }

    return () => {
        listeners.delete(listener);
    };
}

function fetchGeo(signal?: AbortSignal): Promise<GeoResult> {
    if (inflightGeoRequest) return inflightGeoRequest;

    inflightGeoRequest = fetch("/api/geo", { signal })
        .then((response) => {
            if (!response.ok) throw new Error(`Geo API returned ${response.status}`);
            return response.json() as Promise<{ country: string | null; consentTier?: ConsentTier }>;
        })
        .then(({ country, consentTier }) => {
            const c = country ? country.toUpperCase() : null;
            const tier: ConsentTier = consentTier ?? getConsentTier(c);
            return { country: c, consentTier: tier };
        })
        .catch((error): GeoResult => {
            console.warn("Geo detection failed:", error);
            return { country: null, consentTier: "strict" };
        })
        .then((result) => {
            cachedGeo = result;
            if (result.country) notifyListeners(result.country);
            return result;
        })
        .finally(() => {
            inflightGeoRequest = null;
        });

    return inflightGeoRequest;
}

/**
 * Resolves the full geo result including consentTier.
 * Used by consent bootstrapping.
 */
export function resolveGeoWithTier(adminOverride?: string | null): Promise<GeoResult> {
    if (adminOverride) {
        const country = adminOverride.toUpperCase();
        const consentTier = getConsentTier(country);
        cachedGeo = { country, consentTier };
        notifyListeners(country);
        return Promise.resolve(cachedGeo);
    }

    if (cachedGeo) return Promise.resolve(cachedGeo);

    return fetchGeo();
}

/**
 * Resolves the country code only.
 * Returns "US" as a fallback so billing/currency hooks always get a valid value.
 */
export function resolveGeoCountry(adminOverride?: string | null): Promise<string> {
    return resolveGeoWithTier(adminOverride).then((r) => r.country ?? "US");
}
